import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Animation for fade-in effects
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const PageContainer = styled.div`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.div`
  position: relative;
  height: 70vh;
  min-height: 500px;
  background-image: url('https://images.unsplash.com/photo-1579154392128-bf8c7ebee541?w=1600&h=800&fit=crop');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%);
  }
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;
    background-attachment: scroll;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  max-width: 800px;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HeroButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  padding: 12px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
  }
`;

// Content Container
const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

// Section Styles
const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: backwards;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

// Grid Layouts
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Card Components
const InfoCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideInLeft} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 35px rgba(0,0,0,0.15);
  }
`;

const InfoCardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoCardText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  color: #475569;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #3b82f6;
    font-weight: bold;
  }
`;

// Doctor Section
const DoctorSection = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 30px;
  padding: 2rem;
  color: white;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  
  @media (min-width: 768px) {
    grid-template-columns: 300px 1fr;
  }
`;

const DoctorImage = styled.img`
  width: 100%;
  max-width: 250px;
  border-radius: 20px;
  box-shadow: 0 20px 35px rgba(0,0,0,0.3);
  margin: 0 auto;
  display: block;
  
  @media (min-width: 768px) {
    max-width: 100%;
  }
`;

const DoctorContent = styled.div``;

const DoctorName = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #60a5fa;
`;

const DoctorTitle = styled.p`
  font-size: 1rem;
  color: #94a3b8;
  margin-bottom: 1rem;
`;

const DoctorDescription = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: #e2e8f0;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
  }
`;

// Image Gallery
const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 15px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (min-width: 768px) {
    height: 200px;
  }
`;

// Warning Box
const WarningBox = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-left: 4px solid #f59e0b;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const WarningTitle = styled.h4`
  color: #92400e;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

// Main Component
const KidneyDisorderPage = () => {
  const handleAppointment = () => {
    alert('Book an appointment with Dr. Raj Kamal');
  };

  const galleryImages = [
    'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=300&fit=crop',
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Understanding Kidney Disorders</HeroTitle>
          <HeroSubtitle>
            Comprehensive care for kidney health | Expert guidance by Dr. Raj Kamal
          </HeroSubtitle>
          <HeroButton as={Link} to="/book-appointment">Book Appointment</HeroButton>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        {/* Importance of Kidneys Section */}
        <Section delay="0.1s">
          <SectionTitle>Why Your Kidneys Matter</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>
                <span>🫀</span> Vital Organs
              </InfoCardTitle>
              <InfoCardText>
                Your kidneys are two bean-shaped organs about the size of your fist, located just below your rib cage. They filter about 120-150 quarts of blood daily to produce 1-2 quarts of urine.
              </InfoCardText>
              <InfoCardText>
                <strong>Key functions include:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Remove waste products and toxins from your blood</ListItem>
                <ListItem>Balance body fluids and electrolytes</ListItem>
                <ListItem>Regulate blood pressure</ListItem>
                <ListItem>Produce red blood cells (through erythropoietin)</ListItem>
                <ListItem>Activate vitamin D for bone health</ListItem>
                <ListItem>Maintain acid-base balance in your body</ListItem>
              </ul>
            </InfoCard>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?w=600&h=400&fit=crop"
                alt="Kidney anatomy"
                style={{ width: '100%', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
              />
              <WarningBox>
                <WarningTitle>⚠️ Warning Signs</WarningTitle>
                <p style={{ color: '#92400e', margin: 0 }}>
                  Fatigue, swelling in ankles/feet, changes in urination, shortness of breath, and metallic taste in mouth may indicate kidney problems.
                </p>
              </WarningBox>
            </div>
          </TwoColumnGrid>
        </Section>

        {/* Diabetes and Kidney Health */}
        <Section delay="0.2s">
          <SectionTitle>Diabetes & Kidney Health</SectionTitle>
          <SectionSubtitle>
            How diabetes affects your kidneys and what you can do about it
          </SectionSubtitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>🩸 Diabetic Kidney Disease</InfoCardTitle>
              <InfoCardText>
                Diabetes is the leading cause of kidney disease, accounting for nearly 44% of all new cases of kidney failure. High blood sugar levels damage the blood vessels in your kidneys over time, making them less efficient at filtering waste.
              </InfoCardText>
              <InfoCardText>
                <strong>Stages of Diabetic Nephropathy:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Stage 1: Hyperfiltration - Kidneys work harder than normal</ListItem>
                <ListItem>Stage 2: Microalbuminuria - Small amounts of protein in urine</ListItem>
                <ListItem>Stage 3: Macroalbuminuria - More protein leakage</ListItem>
                <ListItem>Stage 4: Clinical proteinuria - Significant kidney damage</ListItem>
                <ListItem>Stage 5: End-stage renal disease - Kidney failure</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📊 Prevention & Management</InfoCardTitle>
              <InfoCardText>
                <strong>To protect your kidneys if you have diabetes:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Maintain blood sugar levels within target range (A1C &lt; 7%)</ListItem>
                <ListItem>Keep blood pressure under 130/80 mmHg</ListItem>
                <ListItem>Take ACE inhibitors or ARBs as prescribed</ListItem>
                <ListItem>Get regular kidney function tests (eGFR and urine albumin)</ListItem>
                <ListItem>Follow a kidney-friendly, low-sodium diet</ListItem>
                <ListItem>Avoid NSAIDs like ibuprofen</ListItem>
              </ul>
              <img 
                src="https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=200&fit=crop"
                alt="Blood sugar monitoring"
                style={{ width: '100%', borderRadius: '15px', marginTop: '1rem' }}
              />
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Acute Kidney Disease */}
        <Section delay="0.3s">
          <SectionTitle>Acute Kidney Disease (AKI)</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>⚡ Understanding Acute Kidney Injury</InfoCardTitle>
              <InfoCardText>
                Acute Kidney Injury (AKI) is a sudden episode of kidney failure or damage that happens within a few hours or days. Unlike chronic kidney disease, AKI is often reversible if caught early.
              </InfoCardText>
              <InfoCardText>
                <strong>Common causes include:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Severe dehydration or blood loss</ListItem>
                <ListItem>Certain medications (NSAIDs, some antibiotics)</ListItem>
                <ListItem>Kidney infections or urinary tract blockages</ListItem>
                <ListItem>Contrast dye from imaging tests</ListItem>
                <ListItem>Severe infections like sepsis</ListItem>
                <ListItem>Major surgery or trauma</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🏥 Symptoms & Treatment</InfoCardTitle>
              <InfoCardText>
                <strong>Symptoms to watch for:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Decreased urine output</ListItem>
                <ListItem>Swelling in legs, ankles, or feet</ListItem>
                <ListItem>Shortness of breath</ListItem>
                <ListItem>Fatigue and confusion</ListItem>
                <ListItem>Chest pain or pressure</ListItem>
              </ul>
              <InfoCardText>
                <strong>Treatment depends on the cause but may include:</strong> IV fluids, medications, dialysis, or treating the underlying condition. Most people recover fully with prompt treatment.
              </InfoCardText>
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Chronic Kidney Disease */}
        <Section delay="0.4s">
          <SectionTitle>Chronic Kidney Disease (CKD)</SectionTitle>
          <SectionSubtitle>
            Understanding the progressive condition and how to manage it
          </SectionSubtitle>
          <InfoCard>
            <InfoCardTitle>📈 What is CKD?</InfoCardTitle>
            <InfoCardText>
              Chronic Kidney Disease is the gradual loss of kidney function over months or years. It affects approximately 10% of the global population. CKD is classified into 5 stages based on eGFR (estimated glomerular filtration rate):
            </InfoCardText>
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              <ListItem><strong>Stage 1:</strong> eGFR ≥ 90 (normal function with kidney damage)</ListItem>
              <ListItem><strong>Stage 2:</strong> eGFR 60-89 (mildly reduced)</ListItem>
              <ListItem><strong>Stage 3:</strong> eGFR 30-59 (moderately reduced)</ListItem>
              <ListItem><strong>Stage 4:</strong> eGFR 15-29 (severely reduced)</ListItem>
              <ListItem><strong>Stage 5:</strong> eGFR &lt; 15 (kidney failure - requires dialysis or transplant)</ListItem>
            </ul>
          </InfoCard>

          <InfoCard style={{ marginTop: '1.5rem' }}>
            <InfoCardTitle>✅ What To Do If Diagnosed With CKD</InfoCardTitle>
            <ThreeColumnGrid>
              <div>
                <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>🥗 Dietary Changes</h4>
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                  <ListItem>Limit sodium to &lt; 2300mg/day</ListItem>
                  <ListItem>Control protein intake</ListItem>
                  <ListItem>Monitor potassium and phosphorus</ListItem>
                  <ListItem>Stay hydrated appropriately</ListItem>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>💊 Medical Management</h4>
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                  <ListItem>Control blood pressure (&lt; 130/80)</ListItem>
                  <ListItem>Manage blood sugar if diabetic</ListItem>
                  <ListItem>Take prescribed medications regularly</ListItem>
                  <ListItem>Annual kidney function monitoring</ListItem>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>🏃 Lifestyle Modifications</h4>
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                  <ListItem>Regular exercise (30 min/day)</ListItem>
                  <ListItem>Avoid smoking and alcohol</ListItem>
                  <ListItem>Maintain healthy weight</ListItem>
                  <ListItem>Stress management techniques</ListItem>
                </ul>
              </div>
            </ThreeColumnGrid>
          </InfoCard>
        </Section>

        {/* Dr. Raj Kamal Section */}
       {/*  <Section delay="0.5s">
          <DoctorSection>
            <DoctorGrid>
              <DoctorImage 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop"
                alt="Dr. Raj Kamal"
              />
              <DoctorContent>
                <DoctorName>Dr. Raj Kamal, MD</DoctorName>
                <DoctorTitle>Senior Nephrologist & Kidney Transplant Specialist</DoctorTitle>
                <DoctorDescription>
                  With over 15 years of experience in nephrology, Dr. Raj Kamal has helped thousands of patients manage kidney disorders and improve their quality of life. His patient-centered approach combines cutting-edge medical treatments with compassionate care.
                </DoctorDescription>
                <DoctorDescription>
                  <strong>How Dr. Raj Kamal can help you:</strong>
                </DoctorDescription>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: '1rem' }}>
                  <ListItem style={{ color: '#e2e8f0' }}>Comprehensive kidney function assessment and early detection</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>Personalized treatment plans for all stages of CKD</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>Expert management of diabetic kidney disease and hypertension</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>Advanced dialysis options (hemodialysis and peritoneal dialysis)</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>Kidney transplant evaluation and post-transplant care</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>Nutritional counseling with renal dietitians</ListItem>
                  <ListItem style={{ color: '#e2e8f0' }}>24/7 patient support and telemedicine consultations</ListItem>
                </ul>
                <CTAButton onClick={handleAppointment}>Schedule Consultation →</CTAButton>
              </DoctorContent>
            </DoctorGrid>
          </DoctorSection>
        </Section> */}

        {/* Image Gallery */}
    {/*     <Section delay="0.6s">
          <SectionTitle>Medical Facilities & Care</SectionTitle>
          <ImageGallery>
            {galleryImages.map((img, index) => (
              <GalleryImage key={index} src={img} alt={`Medical facility ${index + 1}`} />
            ))}
          </ImageGallery>
        </Section> */}

        {/* Call to Action */}
        <Section delay="0.7s">
          <div style={{ 
            background: 'linear-gradient(135deg, #2563eb, #1e40af)', 
            borderRadius: '20px', 
            padding: '2rem', 
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Take Control of Your Kidney Health Today</h3>
            <p style={{ marginBottom: '1.5rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
              Early detection and proper management can slow the progression of kidney disease and help you live a fuller, healthier life.
            </p>
            <HeroButton onClick={handleAppointment} style={{ background: 'white', color: '#2563eb' }}>
              Book Your Appointment Now
            </HeroButton>
          </div>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default KidneyDisorderPage;